/**
 * SecureChat — End-to-end encrypted chat between a patient and a provider.
 *
 * Visual design mirrors the existing MessageThread component so the two
 * experiences feel consistent. The key difference is the encryption badge
 * and status indicator in the header, plus the automatic en/decryption of
 * every message and file attachment.
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { format } from 'date-fns';
import {
  Lock,
  LockOpen,
  Send,
  Paperclip,
  ArrowLeftCircle,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
  X,
  Download,
} from 'lucide-react';
import { useSecureMessages, type KeyExchangeStatus } from '../../hooks/use-secure-messages';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A decrypted message ready to display in the UI.
 * The `id` is generated locally because these messages are never persisted to
 * the server in plaintext — only the encrypted ciphertext payload is stored.
 */
export interface SecureMessageDisplay {
  id: string;
  /** Plain-text content after decryption. */
  content: string;
  /** ISO 8601 timestamp. */
  timestamp: string;
  /** Whether the local user sent this message. */
  isSelf: boolean;
  /** Encrypted payload — kept so we can re-decrypt if the session key rotates. */
  encryptedCiphertext: string;
  encryptedIv: string;
  /** Optional file attachment metadata. */
  attachment?: {
    fileName: string;
    mimeType: string;
    encryptedData: ArrayBuffer;
    iv: string;
    /** Object URL for download, created once successfully decrypted. */
    objectUrl?: string;
  };
}

/**
 * Minimal provider representation needed by SecureChat.
 */
export interface SecureChatProvider {
  id: string;
  name: string;
  role: string;
  /** The provider's ECDH public key as a JWK, obtained from the server. */
  publicKeyJwk?: JsonWebKey;
}

export interface SecureChatProps {
  /** The authenticated patient's user ID. */
  localUserId: string;
  /** The provider this conversation is with. */
  provider: SecureChatProvider;
  /** Called when the user taps the back button on mobile. */
  onBack: () => void;
  /**
   * Called when the local public key is first generated (or rotated).
   * The app should persist this JWK to the server so the provider can
   * perform their half of the ECDH exchange.
   */
  onPublicKeyReady?: (jwk: JsonWebKey) => void;
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

/** Maps key-exchange status values to human-readable labels. */
function statusLabel(status: KeyExchangeStatus): string {
  switch (status) {
    case 'idle':
      return 'Initialising…';
    case 'generating':
      return 'Generating keys…';
    case 'awaiting-remote-key':
      return 'Waiting for provider key…';
    case 'ready':
      return 'End-to-End Encrypted';
    case 'rotating':
      return 'Rotating session keys…';
    case 'error':
      return 'Encryption error';
  }
}

/** Returns TailwindCSS colour classes based on the current key-exchange status. */
function statusColour(status: KeyExchangeStatus): string {
  switch (status) {
    case 'ready':
      return 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800';
    case 'error':
      return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    default:
      return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
  }
}

/** Formats bytes into a human-readable size string. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Key Exchange Prompt sub-component
// ---------------------------------------------------------------------------

interface KeyExchangePromptProps {
  localPublicKeyJwk: JsonWebKey | null;
  providerName: string;
  status: KeyExchangeStatus;
  onCopyPublicKey: () => void;
  onEnterProviderKey: (jwk: JsonWebKey) => void;
  errorMessage: string | null;
}

const KeyExchangePrompt: React.FC<KeyExchangePromptProps> = ({
  localPublicKeyJwk,
  providerName,
  status,
  onCopyPublicKey,
  onEnterProviderKey,
  errorMessage,
}) => {
  const [rawJwkInput, setRawJwkInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSubmitProviderKey = () => {
    setParseError(null);
    try {
      const parsed = JSON.parse(rawJwkInput) as JsonWebKey;
      onEnterProviderKey(parsed);
    } catch {
      setParseError('Invalid JSON — paste the exact key provided by your provider.');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center flex-1 p-6 text-center gap-5">
      <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
        <LockOpen className="w-8 h-8 text-amber-600 dark:text-amber-400" />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
          Set Up Encrypted Channel
        </h3>
        <p className="text-sm text-[var(--text-secondary)] max-w-sm">
          To start a secure conversation with {providerName}, you need to exchange
          encryption keys. This is a one-time setup per conversation.
        </p>
      </div>

      {/* Step 1 — share local public key */}
      {localPublicKeyJwk && (
        <div className="w-full max-w-sm rounded-lg border border-[var(--border-subtle)] p-4 text-left">
          <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
            Step 1 — Share your public key with {providerName}
          </p>
          <div className="bg-[var(--background-subtle)] rounded p-2 text-xs font-mono text-[var(--text-secondary)] break-all max-h-24 overflow-y-auto">
            {JSON.stringify(localPublicKeyJwk)}
          </div>
          <button
            type="button"
            onClick={onCopyPublicKey}
            className="mt-2 text-xs text-[var(--brand-primary)] hover:underline"
          >
            Copy to clipboard
          </button>
        </div>
      )}

      {/* Step 2 — paste provider's public key */}
      <div className="w-full max-w-sm rounded-lg border border-[var(--border-subtle)] p-4 text-left">
        <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
          Step 2 — Paste {providerName}&apos;s public key
        </p>
        <textarea
          value={rawJwkInput}
          onChange={(e) => setRawJwkInput(e.target.value)}
          rows={3}
          placeholder='{"kty":"EC","crv":"P-256", ...}'
          className="w-full p-2 border border-[var(--border-subtle)] rounded text-xs font-mono bg-[var(--background-surface)] text-[var(--text-primary)] resize-none"
        />
        {parseError && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{parseError}</p>
        )}
        <button
          type="button"
          onClick={handleSubmitProviderKey}
          disabled={!rawJwkInput.trim() || status === 'generating'}
          className="mt-2 w-full py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 transition-colors"
        >
          {status === 'generating' ? 'Importing…' : 'Activate Encrypted Channel'}
        </button>
      </div>

      {errorMessage && (
        <p className="text-xs text-red-600 dark:text-red-400 max-w-sm">{errorMessage}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// File Attachment sub-component
// ---------------------------------------------------------------------------

interface AttachmentBubbleProps {
  fileName: string;
  mimeType: string;
  objectUrl?: string;
  encryptedDataSize: number;
}

const AttachmentBubble: React.FC<AttachmentBubbleProps> = ({
  fileName,
  mimeType,
  objectUrl,
  encryptedDataSize,
}) => (
  <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-black/10 dark:bg-white/10">
    <Paperclip className="w-4 h-4 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium truncate">{fileName}</p>
      <p className="text-xs opacity-70">{mimeType} · {formatBytes(encryptedDataSize)}</p>
    </div>
    {objectUrl ? (
      <a
        href={objectUrl}
        download={fileName}
        className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 flex-shrink-0"
        title="Download decrypted file"
      >
        <Download className="w-4 h-4" />
      </a>
    ) : (
      <span className="text-xs opacity-60">Decrypting…</span>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main SecureChat component
// ---------------------------------------------------------------------------

/**
 * SecureChat renders an end-to-end encrypted messaging interface.
 *
 * When the key exchange is complete ({@link KeyExchangeStatus} === `'ready'`),
 * every message is encrypted before being passed to the API and decrypted
 * immediately after receipt.  A green lock badge in the header confirms the
 * active encryption state to the user.
 */
const SecureChat: React.FC<SecureChatProps> = ({
  localUserId,
  provider,
  onBack,
  onPublicKeyReady,
}) => {
  const {
    isE2EEnabled,
    keyStatus,
    localPublicKeyJwk,
    registerRemotePublicKey,
    encryptOutgoing,
    decryptIncoming,
    encryptOutgoingFile,
    decryptIncomingFile,
    rotateSessionKeys,
    errorMessage,
  } = useSecureMessages(localUserId, provider.id);

  const [messages, setMessages] = useState<SecureMessageDisplay[]>([]);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [filePickerError, setFilePickerError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notify parent when local public key is available (so it can be sent to server)
  const prevJwkRef = useRef<JsonWebKey | null>(null);
  useEffect(() => {
    if (localPublicKeyJwk && localPublicKeyJwk !== prevJwkRef.current) {
      prevJwkRef.current = localPublicKeyJwk;
      onPublicKeyReady?.(localPublicKeyJwk);
    }
  }, [localPublicKeyJwk, onPublicKeyReady]);

  // Auto-register provider public key if it was already present on mount
  useEffect(() => {
    if (provider.publicKeyJwk && keyStatus === 'awaiting-remote-key') {
      void registerRemotePublicKey(provider.id, provider.publicKeyJwk);
    }
  }, [provider.publicKeyJwk, provider.id, keyStatus, registerRemotePublicKey]);

  // Scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // -------------------------------------------------------------------------
  // Message sending
  // -------------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || !isE2EEnabled || isSending) return;

    setIsSending(true);

    const encrypted = await encryptOutgoing(text);
    if (!encrypted) {
      setIsSending(false);
      return;
    }

    const newMsg: SecureMessageDisplay = {
      id: `local-${Date.now()}`,
      content: text,
      timestamp: new Date().toISOString(),
      isSelf: true,
      encryptedCiphertext: encrypted.ciphertext,
      encryptedIv: encrypted.iv,
    };

    setMessages((prev) => [...prev, newMsg]);
    setInputText('');
    setIsSending(false);

    // In a real integration, you would POST `encrypted` (not `text`) to the API here.
    // e.g.: await apiClient.post('/messages', { ...encrypted, recipientId: provider.id });
  }, [inputText, isE2EEnabled, isSending, encryptOutgoing]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  // -------------------------------------------------------------------------
  // File attachment
  // -------------------------------------------------------------------------

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilePickerError(null);
      const file = e.target.files?.[0];
      if (!file || !isE2EEnabled) return;

      const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
      if (file.size > MAX_SIZE) {
        setFilePickerError('File exceeds the 10 MB limit.');
        return;
      }

      const encResult = await encryptOutgoingFile(file);
      if (!encResult) return;

      // Immediately decrypt to produce an object URL for the sender's own view
      const blob = await decryptIncomingFile(encResult.encryptedData, encResult.iv);
      const objectUrl = blob ? URL.createObjectURL(blob) : undefined;

      const newMsg: SecureMessageDisplay = {
        id: `local-file-${Date.now()}`,
        content: `Attached: ${file.name}`,
        timestamp: new Date().toISOString(),
        isSelf: true,
        encryptedCiphertext: '',
        encryptedIv: '',
        attachment: {
          fileName: encResult.fileName,
          mimeType: encResult.mimeType,
          encryptedData: encResult.encryptedData,
          iv: encResult.iv,
          objectUrl,
        },
      };

      setMessages((prev) => [...prev, newMsg]);

      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [isE2EEnabled, encryptOutgoingFile, decryptIncomingFile],
  );

  // Simulate receiving an incoming encrypted message (for demo purposes)
  const simulateIncoming = useCallback(async () => {
    if (!isE2EEnabled) return;

    const demoText = 'Hello! This is an end-to-end encrypted response from your provider.';
    const encrypted = await encryptOutgoing(demoText);
    if (!encrypted) return;

    const decrypted = await decryptIncoming(encrypted.ciphertext, encrypted.iv);
    if (!decrypted) return;

    const incomingMsg: SecureMessageDisplay = {
      id: `remote-${Date.now()}`,
      content: decrypted,
      timestamp: new Date().toISOString(),
      isSelf: false,
      encryptedCiphertext: encrypted.ciphertext,
      encryptedIv: encrypted.iv,
    };

    setMessages((prev) => [...prev, incomingMsg]);
  }, [isE2EEnabled, encryptOutgoing, decryptIncoming]);

  // -------------------------------------------------------------------------
  // Copy public key to clipboard
  // -------------------------------------------------------------------------

  const handleCopyPublicKey = useCallback(() => {
    if (!localPublicKeyJwk) return;
    void navigator.clipboard.writeText(JSON.stringify(localPublicKeyJwk));
  }, [localPublicKeyJwk]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  /** True when a key-exchange prompt should be shown instead of the message list. */
  const needsKeyExchange =
    keyStatus === 'awaiting-remote-key' ||
    keyStatus === 'idle' ||
    keyStatus === 'generating';

  /** True when the message list (or secure empty state) should render. */
  const showMessageArea = !needsKeyExchange && keyStatus !== 'error';

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Mobile back button */}
      <div className="md:hidden flex-shrink-0 p-2 bg-[var(--background-subtle)]">
        <button
          onClick={onBack}
          className="flex items-center px-3 py-2 rounded-md text-[var(--text-primary)] hover:bg-[var(--background-interactive)]"
          aria-label="Back to messages"
        >
          <ArrowLeftCircle size={18} className="mr-2" />
          <span className="font-medium">Back to messages</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-[var(--background-subtle)] flex items-center justify-center">
            <span className="text-[var(--text-secondary)] font-semibold">
              {provider.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h2 className="font-medium text-[var(--text-primary)]">{provider.name}</h2>
            <p className="text-xs text-[var(--text-secondary)]">{provider.role}</p>
          </div>
        </div>

        {/* Encryption status badge */}
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${statusColour(keyStatus)}`}
            title={statusLabel(keyStatus)}
          >
            {keyStatus === 'ready' ? (
              <Lock className="w-3.5 h-3.5" aria-hidden />
            ) : keyStatus === 'error' ? (
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" aria-hidden />
            )}
            <span className="hidden sm:inline">{statusLabel(keyStatus)}</span>
          </div>

          {/* Rotate keys button (debug / advanced) */}
          {isE2EEnabled && (
            <button
              type="button"
              onClick={() => void rotateSessionKeys()}
              className="p-1.5 rounded text-[var(--text-secondary)] hover:bg-[var(--background-subtle)] transition-colors"
              title="Rotate session keys"
              aria-label="Rotate session keys"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* E2E notice banner */}
      {isE2EEnabled && (
        <div className="flex-shrink-0 flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/10 border-b border-emerald-100 dark:border-emerald-900/30">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <p className="text-xs text-emerald-700 dark:text-emerald-300 text-center">
            This conversation is end-to-end encrypted. Only you and your provider can read these messages.
          </p>
        </div>
      )}

      {/* Error banner */}
      {keyStatus === 'error' && errorMessage && (
        <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-900/30">
          <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-700 dark:text-red-300 flex-1">{errorMessage}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-xs text-red-700 dark:text-red-300 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Content area — key exchange prompt or message list */}
      {needsKeyExchange ? (
        <KeyExchangePrompt
          localPublicKeyJwk={localPublicKeyJwk}
          providerName={provider.name}
          status={keyStatus}
          onCopyPublicKey={handleCopyPublicKey}
          onEnterProviderKey={(jwk) => void registerRemotePublicKey(provider.id, jwk)}
          errorMessage={errorMessage}
        />
      ) : showMessageArea ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && isE2EEnabled && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-10">
              <Lock className="w-10 h-10 text-emerald-500 dark:text-emerald-400" />
              <p className="text-sm font-medium text-[var(--text-primary)]">
                Secure channel established
              </p>
              <p className="text-xs text-[var(--text-secondary)] max-w-xs">
                Your messages are protected with AES-GCM-256 encryption. Send your first
                message to {provider.name}.
              </p>
              {/* Demo button — remove in production */}
              <button
                type="button"
                onClick={() => void simulateIncoming()}
                className="mt-2 px-3 py-1.5 text-xs rounded-lg border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)] transition-colors"
              >
                Simulate incoming encrypted message (demo)
              </button>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.isSelf ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${
                  msg.isSelf
                    ? 'bg-[var(--brand-primary)] text-white'
                    : 'bg-white/60 dark:bg-slate-700/50 backdrop-blur-md border border-[var(--border-subtle)] text-[var(--text-primary)]'
                }`}
              >
                {/* Encryption indicator on individual messages */}
                <div className={`flex items-center gap-1 mb-1 ${msg.isSelf ? 'justify-end' : 'justify-start'}`}>
                  <Lock className="w-2.5 h-2.5 opacity-60" aria-label="Encrypted" />
                  <span className="text-[10px] opacity-60">Encrypted</span>
                </div>

                {msg.attachment ? (
                  <AttachmentBubble
                    fileName={msg.attachment.fileName}
                    mimeType={msg.attachment.mimeType}
                    objectUrl={msg.attachment.objectUrl}
                    encryptedDataSize={msg.attachment.encryptedData.byteLength}
                  />
                ) : (
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                )}

                <div
                  className={`text-[10px] mt-1 ${
                    msg.isSelf ? 'text-white/60 text-right' : 'text-[var(--text-secondary)] text-left'
                  }`}
                >
                  {format(new Date(msg.timestamp), 'h:mm a')}
                </div>
              </div>
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>
      ) : null}

      {/* Compose area — only shown when E2E is active */}
      {isE2EEnabled && (
        <div className="flex-shrink-0 p-4 border-t border-[var(--border-subtle)]">
          {/* File picker error */}
          {filePickerError && (
            <div className="flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-300 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-1">{filePickerError}</span>
              <button
                type="button"
                onClick={() => setFilePickerError(null)}
                aria-label="Dismiss error"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); void handleSend(); }}
            className="flex items-end gap-2"
          >
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => void handleFileChange(e)}
              aria-label="Attach encrypted file"
            />

            {/* Attach file button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-full text-[var(--text-secondary)] hover:bg-[var(--background-subtle)] hover:text-[var(--brand-primary)] transition-colors flex-shrink-0"
              title="Attach encrypted file"
              aria-label="Attach encrypted file"
            >
              <Paperclip size={18} />
            </button>

            {/* Text input */}
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Send an encrypted message to ${provider.name}…`}
              className="flex-1 p-3 border border-[var(--border-subtle)] rounded-lg bg-[var(--background-surface)] text-[var(--text-primary)] resize-none min-h-[44px] max-h-[150px] text-sm"
              rows={1}
              onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                const target = e.currentTarget;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
              }}
            />

            {/* Send button */}
            <button
              type="submit"
              disabled={!inputText.trim() || isSending}
              className={`
                p-3 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors
                ${inputText.trim() && !isSending
                  ? 'bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white'
                  : 'bg-[var(--background-subtle)] text-[var(--text-disabled)]'
                }
              `}
              aria-label="Send encrypted message"
            >
              {isSending ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </button>
          </form>

          {/* Footer note */}
          <div className="flex items-center justify-center gap-1 mt-2">
            <Lock className="w-3 h-3 text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">
              Messages are encrypted with AES-GCM-256 before leaving your device
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecureChat;
